import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronDown, HelpCircle } from 'lucide-react';

const Section = styled.section`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${(props) => props.theme.colors.border};
`;

const SectionHeading = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-family: ${(props) => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  color: ${(props) => props.theme.colors.foreground};
  margin: 0 0 1.5rem;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${(props) => props.theme.colors.primary};
  }

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Item = styled.details`
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  background-color: ${(props) => props.theme.colors.card};
  overflow: hidden;

  &[open] {
    border-color: ${(props) => props.theme.colors.primary}40;
  }
`;

const QuestionText = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const QuestionNumber = styled.span`
  color: ${(props) => props.theme.colors.primary};
  font-weight: ${(props) => props.theme.typography.fontWeights.bold};
  flex-shrink: 0;
`;

const Question = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  list-style: none;
  padding: 1rem 1.25rem;
  font-size: 1rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  color: ${(props) => props.theme.colors.foreground};

  &::-webkit-details-marker {
    display: none;
  }

  svg {
    flex-shrink: 0;
    width: 1.125rem;
    height: 1.125rem;
    color: ${(props) => props.theme.colors.mutedForeground};
    transition: transform 0.2s ease;
  }

  details[open] & svg {
    transform: rotate(180deg);
  }

  @media (min-width: 768px) {
    font-size: 1.0625rem;
  }
`;

const Answer = styled.div`
  padding: 0 1.25rem 1.25rem;
  color: ${(props) => props.theme.colors.mutedForeground};
  line-height: 1.7;
  font-size: 0.9375rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

export default function ArticleFaq({ faqs = [] }) {
  const { t } = useTranslation();

  if (!Array.isArray(faqs) || faqs.length === 0) return null;

  return (
    <Section aria-labelledby="article-faq-heading">
      <SectionHeading id="article-faq-heading">
        <HelpCircle aria-hidden="true" />
        {t('article.faqHeading', 'Frequently asked questions')}
      </SectionHeading>
      <ItemList>
        {faqs.map((faq, index) => (
          <Item key={`${index}-${faq.question}`}>
            <Question>
              <QuestionText>
                <QuestionNumber>{index + 1})</QuestionNumber>
                {faq.question}
              </QuestionText>
              <ChevronDown aria-hidden="true" />
            </Question>
            <Answer>{faq.answer}</Answer>
          </Item>
        ))}
      </ItemList>
    </Section>
  );
}
